/*
 * <ymal-fill> — cart drawer "You may also like" top-up controller (PRD v3).
 *
 * The drawer renders complementary products server-side. This element fills the
 * list up to data-limit (10) from, in priority order:
 *   1) recently-viewed products (localStorage "recently-viewed")
 *   2) related / recommended products (Shopify recommendations, intent=related)
 * Cards are deduped by product handle against the cart and anything already shown,
 * so a shopper with no view history still sees a full ~10-item list.
 */
(function () {
  if (window.customElements.get('ymal-fill')) return;

  class YmalFill extends HTMLElement {
    constructor() {
      super();
      this._ran = false;
    }

    connectedCallback() {
      // This element only exists once the drawer's recommendations have been
      // injected (i.e. the drawer is open), so fill right away rather than
      // waiting on visibility. setTimeout (not IntersectionObserver or
      // requestAnimationFrame, both of which pause in a hidden/background tab)
      // guarantees the fill still runs when the tab isn't foregrounded.
      setTimeout(() => this.run(), 0);
    }

    get limit() {
      return parseInt(this.getAttribute('data-limit'), 10) || 10;
    }

    get root() {
      return (window.theme && theme.routes && theme.routes.root_url) ? theme.routes.root_url : '/';
    }

    get list() {
      return this.closest('.ymal-list') || this.parentNode;
    }

    remaining() {
      return this.limit - this.list.querySelectorAll('.ymal-card').length;
    }

    async run() {
      if (this._ran) return;
      this._ran = true;

      // Exclusion set: cart handles + handles already rendered (complementary).
      const seen = new Set();
      // The gift-wrap product must never be recommended (from any source).
      seen.add('make-it-a-gift');
      (this.getAttribute('data-exclude') || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((h) => seen.add(h));
      this.list.querySelectorAll('.ymal-card[data-product-handle]').forEach((c) => {
        seen.add(c.getAttribute('data-product-handle'));
      });

      if (this.remaining() <= 0) return;

      // 1) Recently viewed.
      try {
        const ids = JSON.parse(localStorage.getItem('recently-viewed') || '[]').filter(Boolean).slice(0, 10);
        if (ids.length) {
          const q = ids.map((id) => `id:${id}`).join(' OR ');
          const url =
            `${this.root}search?q=${encodeURIComponent(q)}&resources[limit]=10&resources[type]=product` +
            `&section_id=${this.getAttribute('data-recent-section')}`;
          await this.appendFrom(url, seen);
        }
      } catch (err) {
        console.error('[ymal-fill] recently-viewed', err);
      }

      // 2) Related / recommended (fills whatever is left, incl. the no-history case).
      const driverId = this.getAttribute('data-driver-id');
      if (this.remaining() > 0 && driverId) {
        try {
          const url =
            `${this.root}recommendations/products?section_id=${this.getAttribute('data-related-section')}` +
            `&product_id=${driverId}&intent=related&limit=10`;
          await this.appendFrom(url, seen);
        } catch (err) {
          console.error('[ymal-fill] related', err);
        }
      }
    }

    async appendFrom(url, seen) {
      const res = await fetch(url);
      if (!res.ok) return;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const cards = doc.querySelectorAll('.ymal-card[data-product-handle]');
      for (const card of cards) {
        if (this.remaining() <= 0) break;
        const handle = card.getAttribute('data-product-handle');
        if (!handle || seen.has(handle)) continue;
        seen.add(handle);
        // Append as a child; the element is display:contents so cards join .ymal-list.
        this.appendChild(document.importNode(card, true));
      }
    }
  }

  window.customElements.define('ymal-fill', YmalFill);
})();
